import { GetServerSideProps } from 'next';
import IncomingMessageWithCookies from 'types/IncomingMessageWithCookies';
import React, { Fragment, useEffect } from 'react';
import { css } from '@emotion/react';
import {
  StyledForegroundPicture,
  StyledForegroundImage,
  ForegroundImagesWrapper,
} from '@zola/zola-ui/src/components/PreAuthV3/FeaturedImage.styles';
import { MEDIA_QUERY } from '@zola/zola-ui/src/styles/emotion';
import { WAlbumView, WRelatedLinkView } from '@zola/svc-web-api-ts-client';

import { trackEvent } from '@zola-helpers/client/dist/es/tracking/trackingHelper';
import { useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import NAV_DATA from '@zola-helpers/client/dist/es/constants/navConstants';
import { BackgroundType } from '@zola/zola-ui/src/components/PreAuthV3/FeaturedImage';
import * as BREAKPOINTS from '@zola/zola-ui/src/lib/ui/breakpoints';

// Zola UI Components
import Masthead from '@zola/zola-ui/src/components/PreAuthV3/Masthead';
import ContentTiles from '@zola/zola-ui/src/components/PreAuthV3/ContentTiles';
import Grid from '@zola/zola-ui/src/components/PreAuthV3/Grid';
import Differentiation from '@zola/zola-ui/src/components/PreAuthV3/Differentiation';
import SupportAccordion from '@zola/zola-ui/src/components/PreAuthV3/SupportAccordion';

// Utils
import { fetchNavHTML } from 'libs/server/nav';
import { fetchUser } from 'libs/server/users';

import { getAlbumsList } from 'libs/server/weddingAlbums';
import { trackProductListViewed } from '@zola/tracking-contracts/src/tracking';
import {
  getAlbumStartingPriceVariationsPerSize,
  mapProductListToSegment,
} from 'pages/wedding-albums/shop/utils';
import staticFlags from 'libs/universal/utils/staticFlags';

// Components
import Nav from 'components/Nav';
import Header from 'components/LP/WeddingAlbums/Header';
import { PageWrapper } from 'components/LP/v3/PageWrapper.styles';
import AlbumStyleTile from 'components/LP/WeddingAlbums/AlbumStyleTile';

// Content
import {
  albumImages,
  differentiationContent,
  desktopGridItems,
  FAQItems,
  mobileGridItems,
  reviewItems,
} from 'components/LP/WeddingAlbums/content';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';
import { fetchSEORelatedLinks } from 'libs/server/seo';

// Masthead Images
import { SecondaryNavType } from 'components/Nav/types';
import Footer from 'components/ui/Footer/Footer';
import BackgroundMobile from '../../../../public/static-preauth/images/WeddingAlbums/masthead/background_mobile.jpg';
import BackgroundDesktop from '../../../../public/static-preauth/images/WeddingAlbums/masthead/background_desktop.jpg';
import BackgroundMobileWebp from '../../../../public/static-preauth/images/WeddingAlbums/masthead/background_mobile.webp';
import BackgroundDesktopWebp from '../../../../public/static-preauth/images/WeddingAlbums/masthead/background_desktop.webp';

const leftAlignForeground = css`
  ${ForegroundImagesWrapper} {
    justify-content: start;
  }

  ${StyledForegroundPicture} {
    ${MEDIA_QUERY.DESKTOP_XL} {
      display: flex;
      justify-items: start;
    }
  }

  ${StyledForegroundImage} {
    ${MEDIA_QUERY.DESKTOP_XL} {
      margin-left: 0;
    }
  }
`;

type WeddingAlbumsProps = {
  navHTML: string | null;
  userCtx: UserContext | null;
  albumList: WAlbumView[] | void;
  adviceLinks: WRelatedLinkView[];
};

const WeddingAlbums: React.FC<WeddingAlbumsProps> = ({
  navHTML,
  userCtx,
  albumList = [],
  adviceLinks,
}): JSX.Element | null => {
  useForceUpdateOnWindowResize();

  useEffect(() => {
    /*
     * Trigger tracking event
     */
    if (staticFlags.get('updateSegmentEventsPLV')) {
      trackProductListViewed({
        business_category: 'ALBUM',
        business_unit: 'PAPER',
        entity_type: 'ALBUM',
        filters: [],
        sorts: [],
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore:disable-next-line : TS error because of temp condition to support legacy
        products: mapProductListToSegment(albumList),
      });
    } else {
      trackEvent('Product List Viewed', {
        category: 'photo_albums',
        location: 'PLP',
        products: mapProductListToSegment(albumList),
      });
    }
  }, [albumList]);

  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  // * Map tier with the default variation price
  const albumStartingPricePerType =
    albumList.reduce((list, item) => {
      const currentTypeAndPrice = item.key
        ? {
            [item.key]: (item.default_variation?.price_cents || 0) / 100,
          }
        : {};

      return {
        ...list,
        ...currentTypeAndPrice,
      };
    }, {} as Record<string, number>) || {};

  /*
   * Price matrix data per size
   * Map all variations per size (array key),
   * sorted by price asc (variations array)
   */
  const albumStartingPriceVariations =
    albumList.find(item => item.key === 'classic')?.variations || [];
  const albumStartingPriceVariationsPerSize = getAlbumStartingPriceVariationsPerSize(
    albumStartingPriceVariations
  );

  return (
    <>
      <Header />
      <Nav
        navHTML={navHTML}
        user={userCtx}
        disablePrimaryNavCollapse
        secondaryNavType={SecondaryNavType.ALBUMS}
        showPromo
        activePrimaryLink={NAV_DATA.PRIMARY.ALBUMS}
        activeSecondaryLink={NAV_DATA.SECONDARY.ALBUMS.SHOP}
      />
      <PageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <Masthead
          css={leftAlignForeground}
          btnHref="/wedding-albums/select?album_type=premier"
          btnText="Start your album"
          secondaryBtnHref="/shop/brand/zola-wedding-albums"
          secondaryBtnText="Add to registry"
          isPhotoOnRight
          images={{
            background: {
              parameters: {
                alt: 'Person looking at Wedding Album',
                featuredSrc: BackgroundDesktop,
                jpg: [
                  {
                    breakpoint: BREAKPOINTS.SCREEN_XS_MAX.toString(),
                    url: BackgroundMobile,
                  },
                  {
                    breakpoint: BREAKPOINTS.SCREEN_SM_MAX.toString(),
                    url: BackgroundDesktop,
                  },
                  {
                    breakpoint: BREAKPOINTS.SCREEN_LG.toString(),
                    url: BackgroundDesktop,
                  },
                ],
                webp: [
                  {
                    breakpoint: BREAKPOINTS.SCREEN_XS_MAX.toString(),
                    url: BackgroundMobileWebp,
                  },
                  {
                    breakpoint: BREAKPOINTS.SCREEN_SM_MAX.toString(),
                    url: BackgroundDesktopWebp,
                  },
                  {
                    breakpoint: BREAKPOINTS.SCREEN_LG.toString(),
                    url: BackgroundDesktopWebp,
                  },
                ],
              },
              type: BackgroundType.IMAGE,
            },
          }}
          title={
            <h1>
              Design your <span>wedding album</span> and relive the memories
            </h1>
          }
          valueProps={[
            'Professional quality at the perfect price',
            'Easy to select photos and design layouts with our free album wizard',
            'Make it a gift on your registry',
          ]}
        />
        <ContentTiles
          title={
            <>
              Choose your album <span>style</span>
            </>
          }
        >
          <AlbumStyleTile
            images={albumImages.classic}
            featuredImageSrc={albumImages.classicFeaturedSrc}
            albumInformation={{
              description:
                'Just the essentials: layflat, linen cover, photographic printing, and thick pages.',
              type: 'Classic',
              startingPrice: albumStartingPricePerType.classic,
              buildHref: '/wedding-albums/select?album_type=classic',
            }}
            matrixData={albumStartingPriceVariationsPerSize}
          />
          <AlbumStyleTile
            images={albumImages.premier}
            featuredImageSrc={albumImages.premierFeaturedSrc}
            albumInformation={{
              description:
                'The essentials, with just a little extra, with luxe cover options and extra-thick paper.',
              type: 'Premier',
              startingPrice: albumStartingPricePerType.premier,
              buildHref: '/wedding-albums/select?album_type=premier',
            }}
            matrixData={albumStartingPriceVariationsPerSize}
          />
          <AlbumStyleTile
            images={albumImages.deluxe}
            featuredImageSrc={albumImages.deluxeFeaturedSrc}
            albumInformation={{
              description:
                'Our top-quality option. Think Italian leather cover and extra-thick paper.',
              type: 'Deluxe',
              startingPrice: albumStartingPricePerType.deluxe,
              buildHref: '/wedding-albums/select?album_type=deluxe',
            }}
            matrixData={albumStartingPriceVariationsPerSize}
          />
        </ContentTiles>
        <Grid desktopGridItems={desktopGridItems} mobileGridItems={mobileGridItems} />
        <Differentiation
          content={differentiationContent}
          title={{
            children: (
              <Fragment>
                Create an album as <span>personal</span> as the memories inside
              </Fragment>
            ),
          }}
        />
        <div>
          <SupportAccordion
            advice={{ links: adviceLinks }}
            faqs={FAQItems}
            reviews={reviewItems}
            openSections={['faqs']}
          />
        </div>
        <Footer userCtx={userCtx} />
      </PageWrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<WeddingAlbumsProps> = async ({
  req,
}: {
  req: IncomingMessageWithCookies;
}) => {
  const [navHTML, userCtx, albumList, adviceLinks] = await Promise.all([
    fetchNavHTML(req),
    fetchUser(req),
    getAlbumsList(),
    fetchSEORelatedLinks({
      topicSlug: 'relationship-advice',
      subtopicSlug: 'newly-engaged-couples',
    }),
  ]);

  return {
    props: {
      navHTML,
      userCtx,
      albumList,
      adviceLinks,
    },
  };
};

export default WeddingAlbums;
